import { useState, useRef, useEffect, useCallback } from "react";
import "./ContactSearchBar.css";

import AddContactModal from "./AddContactModal";
import FilterModal from "./FilterModal";
import { fetchContacts } from "../api/contacts";
import { useContacts } from "../context/ContactsContext";
import { useTags } from "../context/TagsContext";

import { FaWhatsapp, FaPhone, FaEnvelope, FaInstagram } from "react-icons/fa";

const methodIcons = {
  Whatsapp: <FaWhatsapp />,
  Telefon: <FaPhone />,
  Email: <FaEnvelope />,
  Instagram: <FaInstagram />,
};

export default function ContactSearchBar({
  onSearch,
  onAddContact,
  onFilter,
  onSelectSuggestion,
  filters,
}) {
  // Hol Filter-Optionen aus Context
  const {
    owners: contextOwners,
    contactMethods: contextContactMethods,
    contactType: contextCustomerTypes,
    optionsLoading,
  } = useContacts();

  const { tags: contextTags } = useTags();

  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  // Pagination (für Infinite Scroll im Dropdown)
  const [nextCursor, setNextCursor] = useState(null);

  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const dropdownRef = useRef(null);
  const seenIdsRef = useRef(new Set()); // Dedupe bei Paginierung

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      // Kurzes, lesbares Format (z. B. 08.11.2025)
      return d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  // --- Vorschläge initial laden (debounced) ---
  const fetchSuggestions = (value) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      const q = value.trim();
      if (!q) {
        setSuggestions([]);
        setIsOpen(false);
        setHighlight(-1);
        setNextCursor(null);
        seenIdsRef.current.clear();
        return;
      }

      // Abbrechen, falls noch ein Request läuft
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        setLoadingSuggestions(true);
        seenIdsRef.current.clear();

        const res = await fetchContacts({ q, limit: 12 });
        const items = Array.isArray(res.items) ? res.items : [];
        const unique = items.filter((it) => {
          if (seenIdsRef.current.has(it.id)) return false;
          seenIdsRef.current.add(it.id);
          return true;
        });

        setSuggestions(unique);
        setIsOpen(true);
        setHighlight(unique.length ? 0 : -1);
        setNextCursor(res.nextCursor ?? null);
      } catch (err) {
        // Silent fail
      } finally {
        setLoadingSuggestions(false);
      }
    }, 200);
  };

  // --- Weitere Vorschläge nachladen (infinite scroll) ---
  const loadMoreSuggestions = useCallback(async () => {
    if (!query.trim()) return;
    if (!nextCursor) return;            // nichts mehr da
    if (loadingSuggestions) return;     // schon am Laden

    try {
      setLoadingSuggestions(true);
      const res = await fetchContacts({ q: query.trim(), limit: 12, cursor: nextCursor });
      const items = Array.isArray(res.items) ? res.items : [];
      const unique = items.filter((it) => {
        if (seenIdsRef.current.has(it.id)) return false;
        seenIdsRef.current.add(it.id);
        return true;
      });
      setSuggestions((prev) => [...prev, ...unique]);
      setNextCursor(res.nextCursor ?? null);
    } catch (err) {
      // Silent fail
    } finally {
      setLoadingSuggestions(false);
    }
  }, [nextCursor, query, loadingSuggestions]);

  const handleDropdownScroll = (e) => {
    const target = e.currentTarget;
    const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 80;
    if (nearBottom) loadMoreSuggestions();
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    // Reset Paginierung, dann laden
    setNextCursor(null);
    fetchSuggestions(value);
  };

  const confirmSearch = () => {
    if (!query.trim()) return;
    onSearch?.(query.trim()); // Hauptliste vom Server laden
    setIsOpen(false);
  };

  const chooseSuggestion = (contact) => {
    setIsOpen(false);
    onSelectSuggestion?.(contact); // z. B. rechts öffnen
  };

  const handleKeyDown = (e) => {
    if (!isOpen || !suggestions.length) {
      if (e.key === "Enter") confirmSearch();
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const pick = suggestions[highlight];
      if (pick) chooseSuggestion(pick);
      else confirmSearch();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsOpen(false);
      setHighlight(-1);
    }
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const handleAdd = (contact) => onAddContact?.(contact);

  const defaultFilters = {
    hasEmail: "both",
    hasPhone: "both",
    customerTypes: [],
    contactMethods: [],
    contactOwners: [],
    tags: [],
    hasEvent: "both",
    canContact: "both",
  };

  const handleApplyFilter = (nextFilters) => onFilter?.(nextFilters);

  return (
    <div className="search-bar">
      <div className="search-input-wrapper">
        <input
          type="search"
          placeholder="Suche nach Name, Firma, E-Mail oder Telefon…"
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-controls="contact-typeahead"
          autoComplete="off"
          data-lpignore="true"
          data-1p-ignore="true"
          data-form-type="other"
          role="combobox"
        />
        <button className="action-button search-btn" onClick={confirmSearch}>
          🔎 Suchen
        </button>

        {isOpen && (
          <div
            id="contact-typeahead"
            className="typeahead-dropdown"
            ref={dropdownRef}
            onScroll={handleDropdownScroll}
          >
            {loadingSuggestions && suggestions.length === 0 && (
              <div className="typeahead-item">Lade…</div>
            )}

            {!loadingSuggestions && suggestions.length === 0 && (
              <div className="typeahead-item empty">Keine Treffer</div>
            )}

            {suggestions.map((c, idx) => {
              const isActive = idx === highlight;
              const upcomingBadge = c.eventIsUpcoming ? "kommend" : "letztes";
              const methodIcon = methodIcons[c.contactMethod] || null;

              return (
                <div
                  key={c.id}
                  className={`typeahead-item${isActive ? " active" : ""} ${
                    c.canContact === false ? "cannot-contact" : ""
                  }`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => chooseSuggestion(c)}
                  onMouseEnter={() => setHighlight(idx)}
                >
                  <div className="ti-row">
                    <div className="ti-main">
                      <div className="ti-name">{c.name}</div>
                      <div className="ti-meta">
                        <span className="ti-company">{c.company || "—"}</span>
                        <span className="ti-dot">•</span>
                        <span className="ti-owner">{c.ownerUsername || "—"}</span>
                      </div>
                    </div>
                    <div className="ti-method" title={c.contactMethod || ""}>
                      {methodIcon && <span className="ti-method-icon">{methodIcon}</span>}
                      {!methodIcon && (c.contactMethod || "—")}
                    </div>
                  </div>

                  {(c.eventDay || c.eventTitle) && (
                    <div className="ti-event">
                      <span className={`ti-badge ${c.eventIsUpcoming ? "upcoming" : "past"}`}>
                        {upcomingBadge}
                      </span>
                      <span className="ti-event-title">{c.eventTitle || "Event"}</span>
                      <span className="ti-event-date">{formatDate(c.eventDay)}</span>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Footer: Ladeanzeige / Ende */}
            {loadingSuggestions && suggestions.length > 0 && (
              <div className="typeahead-footer">Lade weitere…</div>
            )}
            {!loadingSuggestions && !nextCursor && suggestions.length > 0 && (
              <div className="typeahead-footer end">Ende der Liste</div>
            )}
          </div>
        )}
      </div>

      <div className="button-group">
        <button
          className="action-button"
          id="open-add-contact"
          onClick={() => setShowAdd(true)}
        >
          ➕ Kontakt
        </button>
        <button
          className="action-button"
          id="open-filter"
          onClick={() => setShowFilter(true)}
        >
          ⚙️ Filter
        </button>
      </div>

      {/* Modals */}
      <AddContactModal open={showAdd} onClose={() => setShowAdd(false)} onSubmit={handleAdd} />
      <FilterModal
        open={showFilter}
        onClose={() => setShowFilter(false)}
        initial={filters}
        onApply={handleApplyFilter}
        onClear={() => onFilter?.(defaultFilters)}
        customerTypes={contextCustomerTypes || []}
        contactMethods={contextContactMethods || []}
        owners={contextOwners || []}
        allTags={contextTags || []}
      />
    </div>
  );
}
